import { Box, Paper, Typography, alpha, Grid, IconButton, Button } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import DiamondIcon from '@mui/icons-material/Diamond';
import PersonIcon from '@mui/icons-material/Person';

import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { Eye } from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import TopGainersLosersModal from './components/TopGainersLosersModal';
import ActiveTradesDetailsModal from './components/ActiveTradesDetailsModal';
import DailyOpenPLModal from '../../../components/DailyOpenPLModal';

// Right side panel layout (55% width) with consistent card sizing
const HorizontalCardsPanel = styled(Box)(({ theme }) => ({
    width: '100%',
    minHeight: 'calc(100vh - 200px)',
    backgroundColor: 'transparent',
    padding: theme.spacing(0.25),
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    overflow: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: alpha(theme.palette.grey[300], 0.2),
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: alpha(theme.palette.grey[500], 0.3),
        borderRadius: '3px',
        '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.5),
        }
    },
    [theme.breakpoints.down('md')]: {
        minHeight: 'auto',
        gap: theme.spacing(0.2), // Reduced from 0.5 to 0.2
        padding: theme.spacing(0.1),
    },
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.3), // Reduced from 0.8 to 0.3
        padding: theme.spacing(0.25),
    }
}));

// System Overview Section with better spacing
const SystemOverviewSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    marginBottom: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    '& .section-title': {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.1),
        // padding: theme.spacing(0.25, 0),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        display: 'inline-block',
    }
}));

// Cards Grid Layout with consistent spacing
const CardsGrid = styled(Grid)(({ theme }) => ({
    margin: 0,
    width: '100%',
    '& .MuiGrid-item': {
        display: 'flex',
        paddingLeft: theme.spacing(0.5),
    },
    '& .MuiGrid-container': {
        margin: 0,
        width: '100%',
    }
}));

const cardColors = [
    {
        background: 'linear-gradient(135deg, #FF9066 0%, #FF5722 100%)', // Orange gradient
        textColor: '#FFFFFF',
        iconColor: '#FFE0B2'
    },
    {
        background: 'linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)', // Blue gradient  
        textColor: '#FFFFFF',
        iconColor: '#BBDEFB'
    },
    {
        background: 'linear-gradient(135deg, #AB47BC 0%, #8E24AA 100%)', // Purple gradient
        textColor: '#FFFFFF',
        iconColor: '#E1BEE7'
    }
];

// Stat card styles - Dynamic height for right panel
const StatsCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'colorIndex'
})(({ theme, colorIndex }) => ({
    padding: theme.spacing(1),
    minHeight: 'auto',
    width: '100%',
    background: cardColors[colorIndex]?.background || cardColors[0].background,
    color: cardColors[colorIndex]?.textColor || '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    },
    [theme.breakpoints.down('lg')]: {
        padding: theme.spacing(0.9),
    },
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(0.8),
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.7),
    }
}));

const StatItem = styled(Box)(({ theme, isGrossPL }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
    '& .MuiTypography-body2': {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.9)',
        letterSpacing: '0.01em',
    },
    '& .MuiTypography-subtitle1': {
        fontSize: '0.85rem',
        fontWeight: 700,
        color: '#FFFFFF',
        letterSpacing: '0.02em',
    },
    ...(isGrossPL && {
        marginTop: theme.spacing(0.25),
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '8px',
        padding: theme.spacing(0.5, 0.75),
        border: '1px solid rgba(255, 255, 255, 0.2)',
        '& .MuiTypography-subtitle1': {
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '0.9rem'
        },
        '& .MuiTypography-body2': {
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.95)',
        }
    }), '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
    },
    '&:last-child': {
        borderBottom: 'none',
        paddingBottom: 0
    },
    '&:first-of-type': {
        paddingTop: 0
    }
}));

const TableHeader = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '8px',
    marginBottom: theme.spacing(0.2), // Reduced from 0.5 to 0.2
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& .header-cell': {
        color: '#FFFFFF',
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        textAlign: 'center',
        letterSpacing: '0.03em',
        '&:first-of-type': {
            textAlign: 'left',
        }
    }
}));

const TableRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0.75),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
    },
    '& .symbol': {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#FFFFFF',
        letterSpacing: '0.02em',
    },
    '& .value': {
        fontSize: '0.75rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: theme.spacing(0.4, 0.6),
        borderRadius: '6px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        letterSpacing: '0.02em',
        color: '#FFFFFF',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        '&.positive': {
            color: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
        },
        '&.negative': {
            color: '#F44336',
            backgroundColor: 'rgba(244, 67, 54, 0.15)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
        },
    },
    '& .total': {
        fontWeight: 700,
        color: '#FFFFFF',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
    }
}));

const CompactCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

const CardHeader = styled(Box)(({ theme }) => ({
    margin: theme.spacing(-2.5, -2.5, 2),
    padding: theme.spacing(2.5, 2.5, 2),
    transition: 'background-color 0.3s ease',
    '& .MuiTypography-h6': {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '0.015em'
    }
}));

// White background card for NSE and MCX Stats - Dynamic sizing
const WhiteStatsCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: '#FFFFFF',
    color: theme.palette.text.primary,
    border: '1px solid #E0E0E0',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

// Dynamic PL Overview card - Dynamic sizing
const PLOverviewCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isPositive'
})(({ theme, isPositive }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: isPositive
        ? 'linear-gradient(135deg, #E8F5E8 0%, #F1F8E9 100%)'
        : 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
    color: theme.palette.text.primary,
    border: isPositive
        ? '1px solid #C8E6C9'
        : '1px solid #FFCDD2',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isPositive
        ? '0 2px 8px rgba(76, 175, 80, 0.1)'
        : '0 2px 8px rgba(244, 67, 54, 0.1)', position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: isPositive
            ? '0 4px 12px rgba(76, 175, 80, 0.15)'
            : '0 4px 12px rgba(244, 67, 54, 0.15)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

// White table header for stats cards
const WhiteTableHeader = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.4),
    padding: theme.spacing(0.5), backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    marginBottom: theme.spacing(0.2), // Reduced from 0.4 to 0.2
    border: '1px solid #E0E0E0',
    '& .header-cell': {
        color: theme.palette.text.primary,
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        textAlign: 'center',
        letterSpacing: '0.03em',
        '&:first-of-type': {
            textAlign: 'left',
        }
    }
}));

// White table row for stats cards
const WhiteTableRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.4),
    padding: theme.spacing(0.4, 0.5),
    borderBottom: '1px solid #F0F0F0',
    alignItems: 'center',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: '#F8F8F8',
        borderRadius: '6px',
    },
    '& .symbol': {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '0.02em',
    },
    '& .value': {
        fontSize: '0.7rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: theme.spacing(0.3, 0.5),
        borderRadius: '6px',
        backgroundColor: '#F5F5F5',
        letterSpacing: '0.02em',
        color: theme.palette.text.primary,
        border: '1px solid #E0E0E0',
        '&.positive': {
            color: '#2E7D32',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
        },
        '&.negative': {
            color: '#C62828',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
        },
    },
    '& .total': {
        fontWeight: 700,
        color: theme.palette.text.primary,
        backgroundColor: '#E3F2FD',
        border: '1px solid #90CAF9',
    }
}));

// Helper function to safely get numeric values
function safeNumeric(value) {
    if (value === undefined || value === null || value === '') {
        return 0;
    }
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
}

// Method to round and format a number in Indian number system format (integer)
function formatValue(value) {
    // Handle undefined, null, or NaN values
    if (value === undefined || value === null || Number.isNaN(value)) {
        return '0';
    }
    const roundedValue = Math.round(value);
    // Format using Indian number system (e.g., 11,00,000 instead of 1,100,000)
    return roundedValue.toLocaleString('en-IN') === '-0' ? '0' : roundedValue.toLocaleString('en-IN');
}

// Method to format a number with one decimal place
function formatValueDecimal(value) {
    // Handle undefined, null, or NaN values
    if (value === undefined || value === null || Number.isNaN(value)) {
        return '0.0';
    }
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
        return '0.0';
    }
    // Format with one decimal place
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const getValueColor = (value) => {
    if (typeof value === 'string') {
        return value.includes('-') ? '#c62828' : '#1b5e20'; // Dark red and dark green
    }
    return value < 0 ? '#c62828' : '#1b5e20'; // Dark red and dark green
};

const DashboardCardsPanel = ({ balanceDetail = {}, userCounts = {}, onUsernameClick, selectedBroker, selectedAdmin, isBrokerView, isAdminView, applySharing = false }) => {
    // Import useAuth to get user role information
    const { user } = useAuth();

    // State for Top Gainers/Losers Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSegment, setModalSegment] = useState('NSE');

    // State for Active Trades Details Modal
    const [activeTradesModalOpen, setActiveTradesModalOpen] = useState(false);
    const [activeTradesSegment, setActiveTradesSegment] = useState('NSE');

    // State for Daily Open PL Modal
    const [dailyPLModalOpen, setDailyPLModalOpen] = useState(false);

    // Get current user ID for API call
    const currentUserId = selectedBroker?.id || selectedAdmin?.id || user?._id;

    // Handler to open top gainers/losers modal
    const handleOpenModal = (segment) => {
        setModalSegment(segment);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    // Handler to open active trades details modal
    const handleOpenActiveTradesModal = (segment) => {
        setActiveTradesSegment(segment);
        setActiveTradesModalOpen(true);
    };

    const handleCloseActiveTradesModal = () => {
        setActiveTradesModalOpen(false);
    };

    // Handler to open daily PL modal
    const handleOpenDailyPLModal = () => {
        setDailyPLModalOpen(true);
    };

    const handleCloseDailyPLModal = () => {
        setDailyPLModalOpen(false);
    };

    // Use decimal format for MCX only when applySharing is true
    const mcxFormatter = applySharing ? formatValueDecimal : formatValue;

    const marketData = {
        nse: [
            { name: 'NIFTY', buy: formatValue(safeNumeric(balanceDetail?.NIFTY?.activeBuy)), sell: formatValue(safeNumeric(balanceDetail?.NIFTY?.activeSell)), total: formatValue(safeNumeric(balanceDetail?.NIFTY?.activeBuy) - safeNumeric(balanceDetail?.NIFTY?.activeSell)) },
            { name: 'BANKNIFTY', buy: formatValue(safeNumeric(balanceDetail?.BANKNIFTY?.activeBuy)), sell: formatValue(safeNumeric(balanceDetail?.BANKNIFTY?.activeSell)), total: formatValue(safeNumeric(balanceDetail?.BANKNIFTY?.activeBuy) - safeNumeric(balanceDetail?.BANKNIFTY?.activeSell)) },
            { name: 'OTHER', buy: formatValue(safeNumeric(balanceDetail?.OTHER_NSE?.activeBuy)), sell: formatValue(safeNumeric(balanceDetail?.OTHER_NSE?.activeSell)), total: formatValue(safeNumeric(balanceDetail?.OTHER_NSE?.activeBuy) - safeNumeric(balanceDetail?.OTHER_NSE?.activeSell)) }
        ],
        mcx: [
            { name: 'GOLD', buy: mcxFormatter(safeNumeric(balanceDetail?.GOLD?.activeBuy)), sell: mcxFormatter(safeNumeric(balanceDetail?.GOLD?.activeSell)), total: mcxFormatter(safeNumeric(balanceDetail?.GOLD?.activeBuy) - safeNumeric(balanceDetail?.GOLD?.activeSell)) },
            { name: 'SILVER', buy: mcxFormatter(safeNumeric(balanceDetail?.SILVER?.activeBuy)), sell: mcxFormatter(safeNumeric(balanceDetail?.SILVER?.activeSell)), total: mcxFormatter(safeNumeric(balanceDetail?.SILVER?.activeBuy) - safeNumeric(balanceDetail?.SILVER?.activeSell)) },
            {
                name: 'MGOLD',
                buy: mcxFormatter(
                    safeNumeric(balanceDetail?.MGOLD?.activeBuy) +
                    safeNumeric(balanceDetail?.GOLDM?.activeBuy)
                ),
                sell: mcxFormatter(
                    safeNumeric(balanceDetail?.MGOLD?.activeSell) +
                    safeNumeric(balanceDetail?.GOLDM?.activeSell)
                ),
                total: mcxFormatter(
                    safeNumeric(balanceDetail?.MGOLD?.activeBuy) +
                    safeNumeric(balanceDetail?.GOLDM?.activeBuy) -
                    (safeNumeric(balanceDetail?.MGOLD?.activeSell) +
                        safeNumeric(balanceDetail?.GOLDM?.activeSell))
                )
            },
            {
                name: 'MSILVER',
                buy: mcxFormatter(
                    safeNumeric(balanceDetail?.MSILVER?.activeBuy) +
                    safeNumeric(balanceDetail?.SILVERM?.activeBuy)
                ),
                sell: mcxFormatter(
                    safeNumeric(balanceDetail?.MSILVER?.activeSell) +
                    safeNumeric(balanceDetail?.SILVERM?.activeSell)
                ),
                total: mcxFormatter(
                    safeNumeric(balanceDetail?.MSILVER?.activeBuy) +
                    safeNumeric(balanceDetail?.SILVERM?.activeBuy) -
                    (safeNumeric(balanceDetail?.MSILVER?.activeSell) +
                        safeNumeric(balanceDetail?.SILVERM?.activeSell))
                )
            },
            { name: 'OTHER', buy: mcxFormatter(safeNumeric(balanceDetail?.OTHER_MCX?.activeBuy)), sell: mcxFormatter(safeNumeric(balanceDetail?.OTHER_MCX?.activeSell)), total: mcxFormatter(safeNumeric(balanceDetail?.OTHER_MCX?.activeBuy) - safeNumeric(balanceDetail?.OTHER_MCX?.activeSell)) }
        ]
    };    // Determine cards based on view and role
    let cards = [];
    let cardGridCols = 4; // Default grid columns for 3 cards

    if (isBrokerView) {
        cards = [
            {
                title: 'Broker',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.your)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.your)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.your)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.your))
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.total)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.total)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.total)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.total))
                }
            },
            {
                title: 'Admin',  // In broker view, the third card is Admin instead of SuperAdmin
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.admin)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.admin)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.admin)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.admin))
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    } else if (isAdminView) {
        cards = [
            {
                title: 'Admin',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.your)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.your)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.your)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.your))
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.total)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.total)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.total)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.total))
                }
            },
            {
                title: 'SuperAdmin',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.admin)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.admin)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.admin)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.admin))
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    } else if (user?.role?.rolename === 'superAdmin') {
        cards = [
            {
                title: 'SuperAdmin',  // Show SuperAdmin for superAdmin role
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.your)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.your)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.your)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.your))
                }
            },
            {
                title: 'Client',  // Show Admin for superAdmin role (second card)
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.total)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.total)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.total)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.total))
                }
            },
            {
                title: 'Admin',  // Show Admin for superAdmin role (second card)
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.admin)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.admin)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.admin)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.admin))
                }
            }
            // Only two cards for SuperAdmin view
        ];
        cardGridCols = 4; // 2 cards with full width (12/2 = 6 each)
    } else {
        cards = [
            {
                title: 'Admin',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.your)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.your)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.your)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.your))
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.total)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.total)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.total)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.total))
                }
            },
            {
                title: 'SuperAdmin',
                stats: {
                    ActivePL: formatValue(safeNumeric(balanceDetail?.activePl?.admin)),
                    BookedPL: formatValue(safeNumeric(balanceDetail?.profitLoss?.admin)),
                    Brokerage: formatValue(safeNumeric(balanceDetail?.brokerage?.admin)),
                    GrossPL: formatValue(safeNumeric(balanceDetail?.grossProfitLoss?.admin))
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    }

    return (
        <HorizontalCardsPanel>
            {/* System Overview Section */}
            <SystemOverviewSection>
                {/* <Typography className="section-title">System Overview</Typography> */}

                {/* Summary Stats Card - Active Users Info */}
                <CompactCard>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'row',
                        gap: { xs: 0.5, sm: 0 }
                    }}>
                        {/* Day to Day Open PL Button - Left side on desktop only */}
                        <Box sx={{
                            display: { xs: 'none', sm: 'flex' }, // Hide on mobile, show on desktop
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 1,
                            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        }}>
                            <Button
                                variant="contained"
                                onClick={handleOpenDailyPLModal}
                                startIcon={<ShowChartIcon />}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    color: '#1976D2',
                                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#FFFFFF',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Day to Day Open PL
                            </Button>
                        </Box>

                        {/* Active Brokers */}
                        {user.role.rolename !== 'broker' && isBrokerView !== true && (<Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 },
                            textAlign: 'center',
                            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                            width: 'auto'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <PersonIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Active Brokers
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Brokers
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'center',
                                gap: { xs: 0.25, sm: 0.5 }
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1rem', sm: '1.5rem' }, // Smaller on mobile
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                }}>
                                    {userCounts?.totalActiveBrokers}
                                </Typography>
                                <Typography sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.9rem' }, // Smaller on mobile
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 500
                                }}>
                                    / {userCounts?.totalBrokers}
                                </Typography>
                            </Box>
                        </Box>)}
                        {/* Active Clients */}
                        <Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 }, // Reduced padding on mobile
                            textAlign: 'center',
                            borderRight: '1px solid rgba(255, 255, 255, 0.2)', // Always show border right
                            width: 'auto' // Remove width constraints
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <PeopleAltIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Active Clients
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Clients
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'center',
                                gap: { xs: 0.25, sm: 0.5 }
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1.1rem', sm: '1.75rem' }, // Smaller on mobile
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                }}>
                                    {userCounts?.totalActiveClients}
                                </Typography>
                                <Typography sx={{
                                    fontSize: { xs: '0.75rem', sm: '1rem' }, // Smaller on mobile
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 500
                                }}>
                                    / {userCounts?.totalClients}
                                </Typography>
                            </Box>
                        </Box>
                        {/* Trading Clients */}
                        <Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 }, // Reduced padding on mobile
                            textAlign: 'center',
                            width: 'auto' // Remove width constraints
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <ShowChartIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Trading Clients
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Trading
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '1.2rem', sm: '2rem' }, // Smaller on mobile
                                lineHeight: 1,
                                fontWeight: 700,
                                color: '#FFFFFF'
                            }}>
                                {userCounts?.totalLiveClients}
                            </Typography>
                        </Box>
                    </Box>
                </CompactCard>

                {/* Day to Day Open PL Button - Mobile View Only */}
                <Box sx={{ mt: 0.5, display: { xs: 'flex', sm: 'none' }, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenDailyPLModal}
                        startIcon={<ShowChartIcon />}
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Day to Day Open PL
                    </Button>
                </Box>
            </SystemOverviewSection>

            {/* Admin, Broker and SuperAdmin Stats Cards - Horizontal Layout */}
            <CardsGrid container spacing={0.5}>
                {cards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={cardGridCols} key={card.title}>
                        <StatsCard colorIndex={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                {card.title === 'Admin' && <SupervisorAccountIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#FFE0B2' }} />}
                                {card.title === 'Broker' && <PersonIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#FFE0B2' }} />}
                                {card.title === 'Clients' && <PeopleAltIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#BBDEFB' }} />}
                                {card.title === 'SuperAdmin' && <AccountBalanceIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#E1BEE7' }} />}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        color: '#FFFFFF',
                                        letterSpacing: '0.02em',
                                        textTransform: 'uppercase'
                                    }}>
                                    {card.title}
                                </Typography>
                            </Box>
                            {Object.entries(card.stats).map(([label, value]) => (
                                <StatItem key={label} isGrossPL={label === 'GrossPL'}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {label === 'ActivePL' && <TrendingUpIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'BookedPL' && <AccountBalanceWalletIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'Brokerage' && <CurrencyExchangeIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'GrossPL' && <MonetizationOnIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }} />}
                                        <Typography variant="body2">{label}</Typography>
                                    </Box>
                                    <Typography variant="subtitle1" style={{
                                        color: label === 'Brokerage' ? "#FFFFFF" : "#FFFFFF",
                                        fontWeight: label === 'GrossPL' ? 800 : 700
                                    }}>
                                        {value}
                                    </Typography>
                                </StatItem>
                            ))}
                        </StatsCard>
                    </Grid>
                ))}
            </CardsGrid>
            {/* NSE and MCX Stats Cards - Side by Side */}
            <CardsGrid container spacing={0.5}>
                <Grid item xs={12} lg={6}>
                    <WhiteStatsCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CandlestickChartIcon sx={{ fontSize: '1.3rem', color: '#1976D2' }} />
                                <Typography
                                    variant="h10"
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: '#1976D2',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    NSE ACTIVE POSITIONS
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenActiveTradesModal('NSE')}
                                startIcon={<Eye size={14} />}
                                sx={{
                                    color: '#1976D2',
                                    borderColor: '#1976D2',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    borderWidth: '1.5px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        borderColor: '#1565C0',
                                        borderWidth: '1.5px'
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px',
                                        marginLeft: 0
                                    }
                                }}
                                title="View Active Trades Details"
                            >
                                Show All
                            </Button>
                        </Box>
                        <WhiteTableHeader>
                            <Typography className="header-cell">Symbol</Typography>
                            <Typography className="header-cell">Buy</Typography>
                            <Typography className="header-cell">Sell</Typography>
                            <Typography className="header-cell">net</Typography>
                        </WhiteTableHeader>
                        {marketData.nse.map((item) => (
                            <WhiteTableRow key={item.name}>
                                <Typography className="symbol">{item.name}</Typography>
                                <Typography className="value positive">+{item.buy.toLocaleString('en-IN')}</Typography>
                                <Typography className="value negative">-{item.sell.toLocaleString('en-IN')}</Typography>
                                <Typography className="value total" style={{ color: getValueColor(item.total) }}>
                                    {item.total.toLocaleString('en-IN')}
                                </Typography>
                            </WhiteTableRow>
                        ))}
                    </WhiteStatsCard>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <WhiteStatsCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DiamondIcon sx={{ fontSize: '1.3rem', color: '#FF9800' }} />
                                <Typography
                                    variant="h10"
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: '#FF9800',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    MCX ACTIVE POSITIONS
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenActiveTradesModal('MCX')}
                                startIcon={<Eye size={14} />}
                                sx={{
                                    color: '#FF9800',
                                    borderColor: '#FF9800',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    borderWidth: '1.5px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                        borderColor: '#F57C00',
                                        borderWidth: '1.5px'
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px',
                                        marginLeft: 0
                                    }
                                }}
                                title="View Active Trades Details"
                            >
                                Show All
                            </Button>
                        </Box>
                        <WhiteTableHeader>
                            <Typography className="header-cell">Symbol</Typography>
                            <Typography className="header-cell">Buy</Typography>
                            <Typography className="header-cell">Sell</Typography>
                            <Typography className="header-cell">net</Typography>
                        </WhiteTableHeader>
                        {marketData.mcx.map((item) => (
                            <WhiteTableRow key={item.name}>
                                <Typography className="symbol">{item.name}</Typography>
                                <Typography className="value positive">+{item.buy}</Typography>
                                <Typography className="value negative">-{item.sell}</Typography>
                                <Typography className="value total" style={{ color: getValueColor(item.total) }}>
                                    {item.total}
                                </Typography>
                            </WhiteTableRow>
                        ))}
                    </WhiteStatsCard>
                </Grid>
            </CardsGrid>            {/* PL Overview Cards - Side by Side */}
            <CardsGrid container spacing={0.5}>
                {/* NSE PL Overview */}
                <Grid item xs={12} lg={6}>
                    <PLOverviewCard isPositive={+balanceDetail?.NSE?.grossProfitLoss?.total >= 0}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1,
                            pb: 0.75,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: +balanceDetail?.NSE?.grossProfitLoss?.total >= 0 ? '#2E7D32' : '#C62828',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em'
                                    }}>
                                    NSE PL Overview
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenModal('NSE')}
                                    sx={{
                                        color: '#2E7D32',
                                        '&:hover': {
                                            backgroundColor: 'rgba(46, 125, 50, 0.1)'
                                        }
                                    }}
                                    title="View Top Gainers & Losers"
                                >
                                    <ShowChartIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                fontWeight: 800,
                                color: +balanceDetail?.NSE?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                letterSpacing: '0.02em',
                            }}>
                                {formatValue(+balanceDetail?.NSE?.grossProfitLoss?.total)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {[
                                { label: 'Nifty', value: formatValue(balanceDetail?.NIFTY?.profitLoss - balanceDetail?.NIFTY?.brokerage) },
                                { label: 'BankNifty', value: formatValue(balanceDetail?.BANKNIFTY?.profitLoss - balanceDetail?.BANKNIFTY?.brokerage) },
                                { label: 'Others', value: formatValue(balanceDetail?.OTHER_NSE?.profitLoss - balanceDetail?.OTHER_NSE?.brokerage) }
                            ].map((item) => (
                                <Box key={item.label}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 0.5,
                                    }}>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#424242',
                                        textTransform: 'capitalize'
                                    }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: item.value.toString().includes('-') ? '#B71C1C' : '#1B5E20',
                                    }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </PLOverviewCard>
                </Grid>

                {/* MCX PL Overview */}
                <Grid item xs={12} lg={6}>
                    <PLOverviewCard isPositive={+balanceDetail?.MCX?.grossProfitLoss?.total >= 0}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1,
                            pb: 0.75,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}>                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: +balanceDetail?.MCX?.grossProfitLoss?.total >= 0 ? '#2E7D32' : '#C62828',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em'
                                    }}>
                                    MCX PL Overview
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenModal('MCX')}
                                    sx={{
                                        color: '#2E7D32',
                                        '&:hover': {
                                            backgroundColor: 'rgba(46, 125, 50, 0.1)'
                                        }
                                    }}
                                    title="View Top Gainers & Losers"
                                >
                                    <ShowChartIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Typography sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                fontWeight: 800,
                                color: +balanceDetail?.MCX?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                letterSpacing: '0.02em',
                            }}>
                                {formatValue(+balanceDetail?.MCX?.grossProfitLoss?.total)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {[
                                {
                                    label: 'Gold', value:
                                        formatValue((balanceDetail?.GOLD?.profitLoss - balanceDetail?.GOLD?.brokerage)
                                            + (balanceDetail?.GOLDM?.profitLoss - balanceDetail?.GOLDM?.brokerage)
                                            + (balanceDetail?.MGOLD?.profitLoss - balanceDetail?.MGOLD?.brokerage))
                                },
                                {
                                    label: 'Silver', value:
                                        formatValue((balanceDetail?.SILVER?.profitLoss - balanceDetail?.SILVER?.brokerage)
                                            + (balanceDetail?.SILVERM?.profitLoss - balanceDetail?.SILVERM?.brokerage)
                                            + (balanceDetail?.MSILVER?.profitLoss - balanceDetail?.MSILVER?.brokerage))
                                },
                                { label: 'Others', value: formatValue(balanceDetail?.OTHER_MCX?.profitLoss - balanceDetail?.OTHER_MCX?.brokerage) }
                            ].map((item) => (
                                <Box key={item.label} sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.5,
                                }}>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#424242',
                                        textTransform: 'capitalize'
                                    }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: item.value.toString().includes('-') ? '#B71C1C' : '#1B5E20',
                                    }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </PLOverviewCard>                </Grid>
            </CardsGrid>

            {/* Top Gainers/Losers Modal */}
            <TopGainersLosersModal
                open={modalOpen}
                onClose={handleCloseModal}
                userId={currentUserId}
                segment={modalSegment}
            />

            {/* Active Trades Details Modal */}
            <ActiveTradesDetailsModal
                open={activeTradesModalOpen}
                onClose={handleCloseActiveTradesModal}
                userId={currentUserId}
                segment={activeTradesSegment}
                applySharing={applySharing}
            />

            {/* Daily Open PL Modal */}
            <DailyOpenPLModal
                open={dailyPLModalOpen}
                onClose={handleCloseDailyPLModal}
            />
        </HorizontalCardsPanel>
    );
};

export default DashboardCardsPanel;
